import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Chip,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  PlayArrow as PlayArrowIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { tasksAPI } from '../../services/api';

/**
 * TaskProgress Component
 * 
 * Displays real-time progress of a Celery background task with live logs.
 * Polls the backend every few seconds to get updates.
 * 
 * Usage:
 *   // Track a specific task by ID (preferred - no race condition)
 *   <TaskProgress taskId={123} pollingInterval={3000} />
 *   
 *   // Or find active task for a job (legacy - may have race condition)
 *   <TaskProgress jobId={4} pollingInterval={3000} />
 */
const TaskProgress = ({ 
  jobId,
  taskId,  // NEW: Specific task ID to track (preferred)
  pollingInterval = 3000,
  onTaskComplete,
  showWhenEmpty = false
}) => {
  const [logsExpanded, setLogsExpanded] = useState(true);
  const logsEndRef = useRef(null);
  const [lastSeenTaskId, setLastSeenTaskId] = useState(null);

  // Track if we should keep polling even without data
  const [keepPolling, setKeepPolling] = useState(true);
  
  // Poll for task - prefer specific taskId, fallback to finding active task for job
  const { 
    data: activeTask, 
    isLoading,
    error 
  } = useQuery(
    taskId ? ['task', taskId] : ['activeTask', jobId],
    () => taskId 
      ? tasksAPI.getTask(taskId)  // Direct lookup - no race condition!
      : tasksAPI.getActiveTask(jobId),  // Legacy: find active task
    {
      enabled: !!(taskId || jobId),
      refetchInterval: (data) => {
        // Keep polling if:
        // 1. No data yet but keepPolling is true (task might be starting)
        // 2. Task is in progress
        if (!data) {
          // Keep polling for tasks that might be starting
          return keepPolling ? pollingInterval : false;
        }
        // Stop polling only when task is explicitly complete
        if (data.status === 'success' || data.status === 'failure') {
          return false;
        }
        return pollingInterval;
      },
      onSuccess: (data) => {
        // Notify when task completes
        if (data && lastSeenTaskId === data.id) {
          if (data.status === 'success' || data.status === 'failure') {
            onTaskComplete?.(data);
          }
        }
        if (data?.id) {
          setLastSeenTaskId(data.id);
          setKeepPolling(true); // Reset keep polling when we see a task
        }
      }
    }
  );
  
  // Reset keepPolling when taskId or jobId changes
  useEffect(() => {
    setKeepPolling(true);
  }, [jobId, taskId]);

  // Auto-scroll to bottom of logs
  useEffect(() => {
    if (logsExpanded && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeTask?.logs, logsExpanded]);

  // Don't render if no task and showWhenEmpty is false
  if (!activeTask && !showWhenEmpty) {
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={20} />
          <Typography color="text.secondary">Checking for active tasks...</Typography>
        </Box>
      </Paper>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load task status: {error.message}
      </Alert>
    );
  }

  // No active task
  if (!activeTask) {
    if (showWhenEmpty) {
      return (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
          <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No active tasks running
          </Typography>
        </Paper>
      );
    }
    return null;
  }

  // Status display helpers
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <ScheduleIcon color="action" />;
      case 'started':
      case 'progress':
        return <PlayArrowIcon color="primary" />;
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'failure':
        return <ErrorIcon color="error" />;
      default:
        return <InfoIcon color="action" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'default';
      case 'started':
      case 'progress':
        return 'primary';
      case 'success':
        return 'success';
      case 'failure':
        return 'error';
      default:
        return 'default';
    }
  };

  const getTaskTypeLabel = (type) => {
    switch (type) {
      case 'extract':
        return 'PDF Extraction';
      case 'match':
        return 'Company Matching';
      case 'enrich':
        return 'Lead Enrichment';
      default:
        return type;
    }
  };

  const getLogIcon = (level) => {
    switch (level) {
      case 'error':
        return <ErrorIcon fontSize="small" color="error" />;
      case 'warning':
        return <WarningIcon fontSize="small" color="warning" />;
      default:
        return <InfoIcon fontSize="small" color="action" />;
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  return (
    <Paper 
      sx={{ 
        p: 2, 
        mb: 2,
        border: 1,
        borderColor: activeTask.status === 'failure' ? 'error.main' : 
                     activeTask.status === 'success' ? 'success.main' : 'primary.main',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {getStatusIcon(activeTask.status)}
          <Typography variant="h6">
            {getTaskTypeLabel(activeTask.task_type)}
          </Typography>
          <Chip 
            label={activeTask.status.toUpperCase()} 
            color={getStatusColor(activeTask.status)}
            size="small"
          />
        </Box>
        
        <Typography variant="caption" color="text.secondary">
          Started: {formatTime(activeTask.started_at)}
        </Typography>
      </Box>

      {/* Progress Bar */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {activeTask.message || 'Processing...'}
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {activeTask.progress_pct}%
          </Typography>
        </Box>
        <LinearProgress 
          variant={activeTask.status === 'started' ? 'indeterminate' : 'determinate'}
          value={activeTask.progress_pct}
          sx={{ height: 8, borderRadius: 1 }}
          color={activeTask.status === 'failure' ? 'error' : 'primary'}
        />
        {activeTask.progress_total > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {activeTask.progress_current} of {activeTask.progress_total} items
          </Typography>
        )}
      </Box>

      {/* Current Item */}
      {activeTask.current_item && (
        <Box sx={{ mb: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="body2">
            <strong>Current:</strong> {activeTask.current_item}
          </Typography>
        </Box>
      )}

      {/* Error Message */}
      {activeTask.error_message && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {activeTask.error_message}
        </Alert>
      )}

      {/* Success Summary */}
      {activeTask.status === 'success' && activeTask.result_summary && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Task completed successfully!
          {activeTask.result_summary.files_processed !== undefined && (
            <Box component="span" sx={{ ml: 1 }}>
              • {activeTask.result_summary.files_processed} files processed
            </Box>
          )}
          {activeTask.result_summary.awards_extracted !== undefined && (
            <Box component="span" sx={{ ml: 1 }}>
              • {activeTask.result_summary.awards_extracted} awards extracted
            </Box>
          )}
          {activeTask.result_summary.leads_enriched !== undefined && (
            <Box component="span" sx={{ ml: 1 }}>
              • {activeTask.result_summary.leads_enriched} leads enriched
            </Box>
          )}
        </Alert>
      )}

      {/* Logs Section */}
      {activeTask.logs && activeTask.logs.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              cursor: 'pointer',
              py: 1,
            }}
            onClick={() => setLogsExpanded(!logsExpanded)}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Logs ({activeTask.logs.length})
            </Typography>
            <IconButton size="small">
              {logsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={logsExpanded}>
            <Box 
              sx={{ 
                maxHeight: 250, 
                overflow: 'auto',
                bgcolor: 'grey.900',
                borderRadius: 1,
                p: 1,
              }}
            >
              <List dense disablePadding>
                {activeTask.logs.map((log, index) => (
                  <ListItem 
                    key={log.id || index}
                    disableGutters
                    sx={{ 
                      py: 0.5,
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box sx={{ mr: 1, mt: 0.5 }}>
                      {getLogIcon(log.log_level)}
                    </Box>
                    <ListItemText
                      primary={
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            color: log.log_level === 'error' ? 'error.light' : 
                                   log.log_level === 'warning' ? 'warning.light' : 'grey.300',
                            wordBreak: 'break-word',
                          }}
                        >
                          {log.message}
                        </Typography>
                      }
                      secondary={
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontFamily: 'monospace',
                            fontSize: '0.7rem',
                            color: 'grey.500',
                          }}
                        >
                          {formatTime(log.created_at)}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
                <div ref={logsEndRef} />
              </List>
            </Box>
          </Collapse>
        </>
      )}
    </Paper>
  );
};

export default TaskProgress;

